import {useEffect, useRef, useState} from 'react';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import useUpdate from '../../../../hooks/useUpdate.js';
import useRemove from '../../../../hooks/useRemove.js';
import useDialogState from '../../../../hooks/useDialogState.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import {ACOES, mensagemSucesso} from './cadastroBasico.js';

const PARAMS_VAZIOS = {};

/*
 * Lógica comum das telas de cadastro básico (Programa, Tipo de Habitação, Tipo
 * de Moradia e Documento): lista, diálogo de criação/edição e exclusão com
 * confirmação. A lista é recarregada do back depois de cada gravação. Os erros
 * (400/401/404/500) já são mostrados pelos hooks de requisição; aqui só entra o
 * aviso de sucesso.
 *
 * `habilitado` = false segura a carga (usuário sem o módulo da tela).
 */
export default function useCadastroBasico({url, nomeItem, habilitado = true}) {
    const [fetch, carregando] = useFetch({returnInnerLoadingState: true});
    const [create, criando] = useCreate({returnInnerLoadingState: true});
    const [update, atualizando] = useUpdate({returnInnerLoadingState: true});
    const [remove, removendo] = useRemove({returnInnerLoadingState: true});
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const {dialogState, openDialog, closeDialog} = useDialogState({cadastro: false});

    const [itens, setItens] = useState([]);
    // Registro em edição; nulo ao adicionar.
    const [itemEmEdicao, setItemEmEdicao] = useState(null);

    // A credencial renova a cada resposta: os callbacks chamam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const carregar = () => fetchRef.current(url, PARAMS_VAZIOS, dados => setItens(Array.isArray(dados) ? dados : []));

    useEffect(() => {
        if (habilitado) {
            carregar();
        }
    }, [habilitado, url]);

    const avisarSucesso = acao => createToast({severity: 'success', message: mensagemSucesso(nomeItem, acao)});

    const adicionar = () => {
        setItemEmEdicao(null);
        openDialog('cadastro')();
    };

    const editar = item => {
        setItemEmEdicao(item);
        openDialog('cadastro')();
    };

    const salvar = payload => {
        const concluir = acao => () => {
            avisarSucesso(acao);
            closeDialog('cadastro')();
            carregar();
        };

        if (itemEmEdicao) {
            update(`${url}/${itemEmEdicao.id}`, payload, concluir(ACOES.ATUALIZAR));
        } else {
            create(url, payload, concluir(ACOES.CRIAR));
        }
    };

    const excluir = item => {
        confirmation('Tem certeza que deseja excluir?', () =>
            remove(`${url}/${item.id}`, () => {
                avisarSucesso(ACOES.EXCLUIR);
                carregar();
            }));
    };

    return {
        itens,
        carregando: carregando || removendo,
        salvando: criando || atualizando,
        dialogAberto: !!dialogState.cadastro,
        fecharDialog: closeDialog('cadastro'),
        itemEmEdicao,
        adicionar,
        editar,
        salvar,
        excluir
    };
}
